import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { paths } from '../../app/routes'
import { useScanMode } from '../../app/scanModeContext'
import { CapacityBar } from '../../components/pallet/CapacityBar'
import { HeroStatusCard, type HeroState } from '../../components/pallet/HeroStatusCard'
import { ItemRow } from '../../components/pallet/ItemRow'
import { CameraViewfinder } from '../../components/scanner/CameraViewfinder'
import { ScanSimulator } from '../../components/scanner/ScanSimulator'
import { useToast } from '../../components/feedback/toastContext'
import { Button } from '../../components/ui/Button'
import { SegmentedToggle } from '../../components/ui/SegmentedToggle'
import { triggerFeedback } from '../../feedback/feedback'
import { useDeviceSettings } from '../../feedback/settings'
import { faNumber } from '../../lib/faNumbers'
import { queueAdd, queueRemove, undoLast } from '../../outbox/operations'
import { db } from '../../outbox/db'
import { useBarcodeScanner } from '../../scanner/useBarcodeScanner'
import { useTorch } from '../../scanner/useTorch'
import { manualRouteFor } from '../Manual/manualTarget'
import { usePalletView } from './usePalletView'
import styles from './PalletScreen.module.css'

const SERIAL_CODE = /^SN-[A-Za-z0-9-]+$/

interface LastScan {
  serialNumber: string
  capturedAt: string
  state: Exclude<HeroState, 'idle'>
}

export function PalletScreen() {
  const { palletCode = '' } = useParams()
  const navigate = useNavigate()
  const { state: routeState } = useLocation()
  const { show } = useToast()
  const { mode, setMode } = useScanMode()
  const { settings } = useDeviceSettings()
  const view = usePalletView(palletCode)

  const [lastScan, setLastScan] = useState<LastScan | null>(null)

  // Remove mode is a property of working on a pallet, not of the session. Leaving the
  // screen in remove mode and coming back to it later is how a scan goes the wrong way.
  useEffect(() => () => setMode('add'), [setMode])

  const record = useCallback(
    async (serialNumber: string, captureMethod: 'camera' | 'manual') => {
      if (view.status === 'closed') {
        triggerFeedback('rejected')
        show('این پالت بسته است و پذیرش ندارد.', 'err')
        return
      }

      const queue = mode === 'add' ? queueAdd : queueRemove
      await queue({ palletCode, serialNumber, captureMethod })
      triggerFeedback('queued')

      // The scan is saved the moment Dexie has it. Nothing waits for the network, which
      // is why the card says «روی دستگاه ذخیره شد» rather than showing a spinner.
      setLastScan({
        serialNumber,
        capturedAt: new Date().toISOString(),
        state: mode === 'add' ? 'queued' : 'removed',
      })
    },
    [mode, palletCode, show, view.status],
  )

  const onDetect = useCallback(
    (value: string) => {
      const code = value.trim().toUpperCase()

      if (!SERIAL_CODE.test(code)) {
        triggerFeedback('rejected')
        show('این بارکد سریال نیست.', 'warn')
        return
      }

      void record(code, 'camera')
    },
    [record, show],
  )

  const scanner = useBarcodeScanner({ enabled: true, onDetect, region: { width: 0.85, height: 0.5 } })
  useTorch(scanner.track, settings.torch)

  // A code typed on the keypad arrives as navigation state, and must be consumed exactly
  // once — a re-render or a back-navigation must not re-enqueue it.
  const consumedManual = useRef<string | null>(null)

  useEffect(() => {
    const manualSerial = (routeState as { manualSerial?: string } | null)?.manualSerial

    if (typeof manualSerial !== 'string' || consumedManual.current === manualSerial) {
      return
    }

    consumedManual.current = manualSerial
    void record(manualSerial, 'manual')
    navigate('.', { replace: true, state: null })
  }, [routeState, record, navigate])

  const onUndo = async () => {
    const outcome = await undoLast(palletCode)

    if (outcome.kind === 'nothing') {
      show('اسکنی برای لغو وجود ندارد', 'warn')
      return
    }

    setLastScan(null)
    show(
      outcome.kind === 'deleted'
        ? 'آخرین اسکن حذف شد'
        : 'درخواست بازگردانی در صف قرار گرفت',
      'ok',
    )
  }

  const onRemoveItem = async (item: { serialNumber: string; opId?: string }) => {
    // A serial still on the device is dropped; one the server holds needs a removal sent.
    if (item.opId !== undefined) {
      const op = await db.outbox.get(item.opId)

      if (op?.status === 'queued') {
        await db.outbox.delete(item.opId)
        show('از صف حذف شد', 'ok')
        return
      }
    }

    await queueRemove({ palletCode, serialNumber: item.serialNumber, captureMethod: 'manual' })
    show('درخواست خارج‌کردن در صف قرار گرفت', 'ok')
  }

  const heroState: HeroState = lastScan?.state ?? 'idle'

  return (
    <div className={styles.screen}>
      <SegmentedToggle mode={mode} onChange={setMode} />

      <CameraViewfinder
        size="strip"
        badgeLabel="اسکن پیوسته فعال"
        hint={mode === 'add' ? 'سریال بعدی را اسکن کنید' : 'برای خارج‌کردن، سریال را اسکن کنید'}
        status={scanner.status}
        error={scanner.error}
        videoRef={scanner.videoRef}
        onActivate={scanner.retry}
      />

      {view.status === 'closed' && (
        <div className={styles.closed}>این پالت بسته است · اسکن تازه پذیرفته نمی‌شود</div>
      )}

      <HeroStatusCard
        state={heroState}
        serialNumber={lastScan?.serialNumber ?? ''}
        capturedAt={lastScan?.capturedAt ?? ''}
        onUndo={() => void onUndo()}
        canUndo={view.pending.length > 0}
      />

      <CapacityBar itemCount={view.itemCount} capacityMax={view.capacityMax} />

      {import.meta.env.DEV && (
        <ScanSimulator onScan={onDetect} duplicateOf={view.items[0]?.serialNumber} />
      )}

      <div className={`${styles.items} scroll-y`}>
        {view.items.length === 0 ? (
          <div className={styles.empty}>
            {view.isStale ? 'اقلام این پالت روی دستگاه ذخیره نشده است.' : 'این پالت خالی است.'}
          </div>
        ) : (
          view.items.map((item, index) => (
            <ItemRow
              key={item.serialNumber}
              index={index + 1}
              serialNumber={item.serialNumber}
              state={item.state}
              onRemove={() => void onRemoveItem(item)}
            />
          ))
        )}
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          compact
          onClick={() => {
            const route = manualRouteFor({ kind: 'serial', palletCode })
            navigate(route.to, { state: route.state })
          }}
        >
          ورود دستی
        </Button>
        <Button
          compact
          onClick={() => {
            show(`${faNumber(view.itemCount)} قلم روی ${palletCode} ثبت شد`, 'ok')
            navigate(paths.home)
          }}
        >
          پایان کار با پالت
        </Button>
      </div>
    </div>
  )
}
